import './styles/tokens.css';
import './styles/v2-primitives.css';
import './styles/shell.css';

import { createA11yPilotApi, withPrimedOverview } from './api/a11ypilot-api.js';
import { isUnauthorizedError, resolveCanManage } from './auth/access.js';
import { renderSignedOut } from './auth/auth-view.js';
import { createFirebaseAuth } from './auth/firebase-auth.js';
import { RuntimeConfigError, readRuntimeConfig } from './config/runtime.js';
import { openAuditDeepLink } from './navigation/audit-deep-link.js';
import {
  createAuthenticatedShell,
  renderFatalState,
  renderInitializing,
  renderShellState,
} from './ui/shell.js';
import { createToastManager } from './ui/toast.js';

const root = document.querySelector('#app');

if (!(root instanceof HTMLElement)) {
  throw new Error('Root #app non disponibile.');
}

const ADMIN_ROLES = new Set(['owner', 'admin']);

const toasts = createToastManager(document.body);
let authClient = null;
let authUnsubscribe = null;
let activeShell = null;
let activeView = null;
let activeUsersView = null;
let activeScreenDispose = null;
let activeSession = null;
let activeScreen = 'audits';
let transitionId = 0;

/** Smonta la vista interna lasciando in piedi lo shell e la sessione. */
function disposeInnerView() {
  try { activeView?.destroy?.(); } catch (_error) { /* best effort */ }
  try { activeUsersView?.destroy?.(); } catch (_error) { /* best effort */ }
  activeView = null;
  activeUsersView = null;
  document.body.classList.remove('a11yp-noscroll');
}

function disposeScreen() {
  disposeInnerView();
  try { activeScreenDispose?.(); } catch (_error) { /* best effort */ }
  try { activeShell?.destroy?.(); } catch (_error) { /* best effort */ }
  activeScreenDispose = null;
  activeShell = null;
  activeSession = null;
  activeScreen = 'audits';
}

function signOutWithFeedback() {
  return authClient.signOut().catch((error) => {
    toasts.notify({
      type: 'error',
      text: error?.message || 'Disconnessione non riuscita. Riprova.',
    });
  });
}

async function transitionToUser(user, config) {
  const ownTransition = ++transitionId;
  disposeScreen();

  if (!user) {
    activeScreenDispose = renderSignedOut(root, {
      authClient,
      toast: toasts.notify,
    });
    return;
  }

  activeShell = createAuthenticatedShell(root, {
    user,
    tenantId: config.tenantId,
    onSignOut: signOutWithFeedback,
    onToggleUsers: () => void toggleUsersScreen(),
  });
  renderShellState(activeShell.main, {
    title: 'Verifica del workspace',
    body: 'Stiamo controllando il tuo accesso ai siti e agli audit.',
    busy: true,
  });

  const api = createA11yPilotApi({
    auth: authClient.auth,
    tenantId: config.tenantId,
    endpoint: config.apiEndpoint,
    getAppCheckToken: authClient.getAppCheckToken,
  });

  let overview;
  try {
    overview = await api.postReport('a11ypilot_overview_get', {});
  } catch (error) {
    if (ownTransition !== transitionId) return;
    if (isUnauthorizedError(error)) {
      renderShellState(activeShell.main, {
        icon: 'lock_person',
        title: 'Workspace non disponibile',
        body: 'Il tuo account è autenticato, ma non è membro di questo workspace. Chiedi l’abilitazione a un amministratore A11yPilot.',
        tone: 'unauthorized',
        actions: [
          { label: 'Riprova', primary: true, onClick: () => void transitionToUser(user, config) },
          { label: 'Esci', onClick: () => void signOutWithFeedback() },
        ],
      });
      return;
    }

    renderShellState(activeShell.main, {
      icon: 'cloud_off',
      title: 'Servizio non raggiungibile',
      body: error?.message || 'Non è stato possibile verificare il workspace. Controlla la connessione e riprova.',
      tone: 'error',
      actions: [
        { label: 'Riprova', primary: true, onClick: () => void transitionToUser(user, config) },
      ],
    });
    return;
  }

  if (ownTransition !== transitionId) return;

  const canManage = resolveCanManage(overview);
  const role = String(overview?.access?.role || '').toLowerCase();
  activeShell.setAccess(canManage);
  /* Il pannello Utenti si mostra a owner e admin. Non è un permesso: l'API
     verifica il ruolo a ogni chiamata e assegnare ruoli resta agli owner. */
  activeShell.setUsersAvailable(ADMIN_ROLES.has(role));
  activeShell.setUsersActive(false);
  activeSession = {
    user,
    config,
    api,
    canManage,
    role,
    transition: ownTransition,
    // L'overview già letta serve una volta sola alla prima apertura degli audit.
    primedOverview: overview,
  };
  await mountAuditsScreen();
}

async function mountAuditsScreen() {
  const session = activeSession;
  if (!session || session.transition !== transitionId) return;
  disposeInnerView();
  activeScreen = 'audits';
  activeShell.setUsersActive(false);

  let mountA11yPilotView;
  try {
    ({ mountA11yPilotView } = await import('./features/a11ypilot/a11ypilot-view.js'));
  } catch (_error) {
    if (session.transition !== transitionId) return;
    renderShellState(activeShell.main, {
      icon: 'error_outline',
      title: 'Interfaccia non disponibile',
      body: 'Non è stato possibile caricare A11yPilot. Controlla la connessione e ricarica la pagina.',
      tone: 'error',
      actions: [
        { label: 'Riprova', primary: true, onClick: () => void transitionToUser(session.user, session.config) },
      ],
    });
    return;
  }
  if (session.transition !== transitionId) return;

  const primed = session.primedOverview;
  session.primedOverview = null;
  activeView = mountA11yPilotView({
    root: activeShell.main,
    navHost: activeShell.nav,
    postReport: primed ? withPrimedOverview(session.api.postReport, primed) : session.api.postReport,
    toast: toasts.notify,
    canManage: session.canManage,
    selfEmail: session.user.email || '',
  });

  if (!activeView) {
    renderShellState(activeShell.main, {
      icon: 'error_outline',
      title: 'Interfaccia non disponibile',
      body: 'A11yPilot non è riuscito ad avviare la vista. Ricarica la pagina per riprovare.',
      tone: 'error',
    });
    return;
  }

  if (!session.deepLinkOpened) {
    session.deepLinkOpened = true;
    try {
      await openAuditDeepLink(activeView, session.config.auditId);
    } catch (error) {
      if (session.transition !== transitionId) return;
      toasts.notify({
        type: 'error',
        text: error?.message || 'Non è stato possibile aprire l\u2019audit indicato nel link.',
      });
    }
  }
}

async function mountUsersScreen() {
  const session = activeSession;
  if (!session || session.transition !== transitionId) return;
  disposeInnerView();
  activeScreen = 'users';
  activeShell.setUsersActive(true);
  /* La barra centrale appartiene alla navigazione degli audit: qui si svuota,
     altrimenti resterebbero tab che non governano nulla. */
  activeShell.nav.innerHTML = '';
  renderShellState(activeShell.main, { title: 'Utenti', body: 'Lettura dell’elenco…', busy: true });

  let mountUsersView;
  try {
    ({ mountUsersView } = await import('./features/users/users-view.js'));
  } catch (_error) {
    if (session.transition !== transitionId) return;
    renderShellState(activeShell.main, {
      icon: 'error_outline',
      title: 'Sezione non disponibile',
      body: 'Non è stato possibile caricare la gestione utenti. Controlla la connessione e riprova.',
      tone: 'error',
      actions: [{ label: 'Torna agli audit', primary: true, onClick: () => void mountAuditsScreen() }],
    });
    return;
  }
  if (session.transition !== transitionId) return;

  activeUsersView = mountUsersView({
    root: activeShell.main,
    postReport: session.api.postReport,
    toast: toasts.notify,
    selfUid: session.user.uid || '',
    sendPasswordReset: (email) => authClient.sendPasswordReset(email),
  });
}

async function toggleUsersScreen() {
  if (!activeSession) return;
  if (activeScreen === 'users') await mountAuditsScreen();
  else await mountUsersScreen();
}

async function bootstrap() {
  renderInitializing(root);

  let config;
  try {
    config = readRuntimeConfig();
  } catch (error) {
    const detail = error instanceof RuntimeConfigError ? error.missing.join(', ') : '';
    renderFatalState(root, {
      title: 'Configurazione incompleta',
      body: 'Questa installazione A11yPilot non è ancora configurata. Contatta chi gestisce il servizio.',
      detail,
    });
    return;
  }

  try {
    authClient = await createFirebaseAuth(config.firebase, {
      appCheckSiteKey: config.appCheckSiteKey,
      justbitSsoEnabled: config.justbitSsoEnabled,
    });
    document.documentElement.dataset.appCheck = authClient.appCheckEnabled ? 'enabled' : 'bootstrap';
  } catch (error) {
    renderFatalState(root, {
      title: 'Autenticazione non disponibile',
      body: 'Non è stato possibile inizializzare l’accesso sicuro ad A11yPilot.',
      detail: error?.code || '',
    });
    return;
  }

  authUnsubscribe = authClient.observe(
    (user) => void transitionToUser(user, config),
    (error) => {
      transitionId += 1;
      disposeScreen();
      renderFatalState(root, {
        title: 'Sessione non disponibile',
        body: 'Non è stato possibile verificare lo stato della sessione. Ricarica la pagina per riprovare.',
        detail: error?.code || '',
      });
    },
  );
}

window.addEventListener('pagehide', () => {
  transitionId += 1;
  disposeScreen();
  authUnsubscribe?.();
  toasts.destroy();
}, { once: true });

void bootstrap();
